/**
 * V2.0 : la liste des 233 pays n'est plus dans le HTML (4 copies par page).
 * Au premier survol / toucher / focus / clic sur le sélecteur, on la récupère une seule fois
 * (section localization-countries) puis on la clone dans la liste de ce sélecteur.
 */
class LocalizationFormComponent extends HTMLElement {
  static countriesPromise = null;

  static fetchCountries() {
    if (!LocalizationFormComponent.countriesPromise) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      LocalizationFormComponent.countriesPromise = fetch(`${root}?section_id=localization-countries`, {
        credentials: 'same-origin',
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = Array.from(doc.querySelectorAll('li[data-ref="country-input-item"]'));
          if (!items.length) throw new Error('Liste des pays vide');
          return items;
        })
        .catch((error) => {
          LocalizationFormComponent.countriesPromise = null;
          throw error;
        });
    }
    return LocalizationFormComponent.countriesPromise;
  }

  constructor() {
    super();
    this.form = null;
    this.languageInput = null;
    this.countryInput = null;
    this.countryList = null;
    this.lazyHost = null;
    this.countriesLoaded = false;
  }

  connectedCallback() {
    // On utilise l'ID exceptionnellement à la place des data-ref car c'est une balise {% form %}
    this.form = this.querySelector('#LocalizationForm');
    this.languageInput = this.querySelector('[data-ref="language-input"]');
    this.countryInput = this.querySelector('[data-ref="country-input"]');
    this.countryList = this.querySelector('[data-ref="country-list"]');

    // Délégation : fonctionne pour le pays pré-rendu comme pour la liste chargée plus tard
    this.countryList?.addEventListener('click', this.handleCountryClick);

    // On écoute les changements de langue
    this.languageInput?.addEventListener('change', this.handleLanguageChange);

    // Chargement différé de la liste complète
    if (this.countryList) {
      this.lazyHost = this.closest('dropdown-component') || this;
      this.bindLazyLoad();
    }
  }

  disconnectedCallback() {
    this.countryList?.removeEventListener('click', this.handleCountryClick);
    this.languageInput?.removeEventListener('change', this.handleLanguageChange);
    this.unbindLazyLoad();
  }

  bindLazyLoad() {
    ['pointerenter', 'pointerdown', 'touchstart', 'focusin', 'click'].forEach((type) => {
      this.lazyHost.addEventListener(type, this.loadCountries, { capture: true, passive: true });
    });
  }

  unbindLazyLoad() {
    if (!this.lazyHost) return;
    ['pointerenter', 'pointerdown', 'touchstart', 'focusin', 'click'].forEach((type) => {
      this.lazyHost.removeEventListener(type, this.loadCountries, { capture: true });
    });
  }

  loadCountries = () => {
    if (this.countriesLoaded) return;
    this.countriesLoaded = true;
    this.unbindLazyLoad();

    LocalizationFormComponent.fetchCountries()
      .then((items) => {
        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(document.importNode(item, true)));
        this.countryList.replaceChildren(fragment);
      })
      .catch(() => {
        // En cas d'échec réseau, on réessaiera au prochain survol/clic
        this.countriesLoaded = false;
        this.bindLazyLoad();
      });
  };

  handleCountryClick = (event) => {
    const item = event.target instanceof Element ? event.target.closest('[data-ref="country-input-item"]') : null;
    if (!item || !this.countryList.contains(item)) return;
    this.selectCountry(item.dataset.value, event);
  };

  handleLanguageChange = (event) => {
    this.changeLanguage(event);
  };

  selectCountry = (countryName, event) => {
    event.preventDefault();

    this.countryInput.value = countryName;
    this.form?.submit();
  };

  changeLanguage(event) {
    const value = event.target instanceof HTMLSelectElement ? event.target.value : null;

    if (value) {
      this.languageInput.value = value;
      this.form?.submit();
    }
  }
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}
